export interface ScrollItemIntoCenterOptions {
  maxFrames?: number; // default 60 (~1s at 60fps) — hard cap
  minFrames?: number; // default 15 (~250ms) — covers Kendo Reveal 200ms
  stableFrames?: number; // default 3 — consecutive stable snapshots required
  attrName?: string; // default "data-tree-item-id"
  rowSelector?: string; // default "li.k-treeview-item"
}

const LAYOUT_EPSILON_PX = 1;

function waitForAnimationFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function findMatchedElement(container: HTMLElement, attrName: string, itemId: string): HTMLElement | null {
  return container.querySelector<HTMLElement>(`[${attrName}="${itemId}"]`);
}

function isScrollableY(el: HTMLElement): boolean {
  if (el.scrollHeight <= el.clientHeight) return false;
  const style = getComputedStyle(el);
  const overflowY = style.overflowY;
  return overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
}

// Find the nearest scrollable-Y ancestor of `start`, walking up but stopping
// at (and including) `boundary`. This avoids scrolling outer containers (e.g.
// the one wrapping the tenant header), which would push unrelated UI out of view.
function findScrollContainer(start: HTMLElement, boundary: HTMLElement): HTMLElement {
  let current: HTMLElement | null = start;
  while (current && current !== boundary.parentElement) {
    if (isScrollableY(current)) return current;
    current = current.parentElement;
  }
  return boundary;
}

export async function scrollItemIntoCenter(
  container: HTMLElement | null,
  itemId: string,
  options?: ScrollItemIntoCenterOptions
): Promise<boolean> {
  if (!container || !itemId) {
    return false;
  }

  const maxFrames = options?.maxFrames ?? 60;
  const minFrames = options?.minFrames ?? 15;
  const stableFrames = options?.stableFrames ?? 3;
  const attrName = options?.attrName ?? "data-tree-item-id";
  const rowSelector = options?.rowSelector ?? "li.k-treeview-item";

  let found = false;
  let stableCount = 0;

  for (let frame = 0; frame < maxFrames; frame++) {
    await waitForAnimationFrame();

    const matched = findMatchedElement(container, attrName, itemId);
    if (!matched) continue;

    found = true;
    const row = matched.closest<HTMLElement>(rowSelector) ?? matched;

    // Resolve the actual scroller: it may be `container` itself OR a descendant
    // that holds the rendered tree (e.g. Kendo TreeView's inner viewport).
    // We deliberately do NOT walk above `container` so that ancestor scrollers
    // (which may also contain the tenant header) are left untouched.
    const scroller = findScrollContainer(row, container);

    const scrollerRect = scroller.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const rowOffsetInScroller = rowRect.top - scrollerRect.top + scroller.scrollTop;
    const desiredScrollTop = rowOffsetInScroller - (scroller.clientHeight - rowRect.height) / 2;
    const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
    const clampedScrollTop = Math.max(0, Math.min(desiredScrollTop, maxScrollTop));

    const delta = Math.abs(scroller.scrollTop - clampedScrollTop);
    scroller.scrollTop = clampedScrollTop;

    if (delta <= LAYOUT_EPSILON_PX) {
      stableCount += 1;
    } else {
      stableCount = 0;
    }

    if (frame + 1 >= minFrames && stableCount >= stableFrames) {
      return true;
    }
  }

  return found;
}
